import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { FlaskConical, AlertTriangle } from "lucide-react";
import { useAuth } from "@/app/auth";
import { supabase } from "@/lib/supabase";
import { usePurgeTestClient } from "./delete";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const COUNT_LABEL: Record<string, string> = {
  factures: "facture(s) d'essai", devis: "devis", commandes: "commande(s)", contrats: "contrat(s)",
  leads: "lead(s) — ils retournent au stock, ils ne sont pas détruits", ventes: "vente(s) de lead", paiements: "paiement(s) en mode test",
};

/**
 * Un client d'essai et tout ce qu'il a servi à tester, d'un coup. Réservé aux
 * clients en mode test : la base refuse pour un vrai client, et refuse aussi
 * s'il porte le moindre paiement réel.
 */
export function PurgeTestClientButton({ clientId, name, onDone }: { clientId: string; name: string; onDone?: () => void }) {
  const { roles } = useAuth();
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState("");
  const [reason, setReason] = useState("");
  const purge = usePurgeTestClient();

  const { data: counts } = useQuery({
    queryKey: ["purge_test_preview", clientId],
    enabled: open,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("purge_test_preview", { _client_id: clientId });
      if (error) throw error;
      return (data as unknown as { contenu: Record<string, number> }).contenu;
    },
  });

  if (!roles.includes("admin")) return null;

  return (
    <>
      <Button variant="outline" className="text-destructive" onClick={() => setOpen(true)}>
        <FlaskConical className="mr-2 h-4 w-4" />Supprimer le client de test
      </Button>
      <Dialog open={open} onOpenChange={(o) => { if (!o) { setOpen(false); setTyped(""); setReason(""); } }}>
        <DialogContent className="max-w-lg" data-testid="purge-test-dialog">
          <DialogHeader>
            <DialogTitle>Supprimer {name} et ses données d'essai</DialogTitle>
            <DialogDescription>Ce client est en mode test : ses documents n'ont aucune valeur comptable. Tout part d'un coup, sans passer par la corbeille.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="rounded-xl border border-destructive/30 bg-destructive/10 p-3 text-sm">
              <div className="mb-1 flex items-center gap-2 font-semibold text-destructive"><AlertTriangle className="h-4 w-4" />Seront supprimés</div>
              {!counts ? <p className="text-muted-foreground">Décompte…</p> : (
                <ul className="list-inside list-disc">
                  {Object.entries(counts).filter(([, n]) => n > 0).map(([k, n]) => <li key={k}>{n} {COUNT_LABEL[k] ?? k}</li>)}
                  {Object.values(counts).every((n) => n === 0) && <li>aucun document : seule la fiche client part</li>}
                </ul>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="purge-reason">Motif *</Label>
              <Textarea id="purge-reason" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Ex. fin des essais de paiement" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="purge-typed">Recopie <span className="font-mono font-semibold">{name}</span> pour confirmer</Label>
              <Input id="purge-typed" value={typed} onChange={(e) => setTyped(e.target.value)} autoComplete="off" />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setOpen(false)}>Retour</Button>
              <Button variant="destructive" data-testid="purge-test-confirm"
                disabled={typed.trim() !== name.trim() || reason.trim().length < 3 || purge.isPending}
                onClick={() => purge.mutate({ clientId, reason: reason.trim() }, { onSuccess: () => { setOpen(false); onDone?.(); } })}>
                Supprimer définitivement
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
